import { useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  getSignedReportsReadyToSend,
  getReportsByStatus,
  getPatientById
} from "@/data/mockData";
import {
  CheckCircle2,
  Send,
  User,
  FileText,
  Calendar,
  Shield,
  Loader2,
  Mail,
  Building2,
  ChevronRight
} from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { it } from "date-fns/locale";

export const DoctorCompletedReports = () => {
  const navigate = useNavigate();
  const { currentDoctor } = useOutletContext();
  const [selectedReports, setSelectedReports] = useState([]);
  const [isSending, setIsSending] = useState(false);

  const signedReports = getSignedReportsReadyToSend().filter(r => r.doctorId === currentDoctor.id);
  const sentReports = getReportsByStatus('sent').filter(r => r.doctorId === currentDoctor.id);

  const toggleSelect = (reportId) => {
    setSelectedReports(prev => 
      prev.includes(reportId)
        ? prev.filter(id => id !== reportId)
        : [...prev, reportId]
    );
  };

  const selectAll = () => {
    if (selectedReports.length === signedReports.length) {
      setSelectedReports([]);
    } else {
      setSelectedReports(signedReports.map(r => r.id));
    }
  };

  const handleSendReports = async () => {
    if (selectedReports.length === 0) {
      toast.error("Seleziona almeno un referto");
      return;
    }

    setIsSending(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIsSending(false);
    
    toast.success(`${selectedReports.length} referti inviati`, {
      description: "I pazienti riceveranno una notifica"
    });
    
    setSelectedReports([]);
  };

  const ReportCard = ({ report, selectable = false }) => {
    const patient = getPatientById(report.patientId);
    const isSelected = selectedReports.includes(report.id);

    return (
      <Card
        className={cn(
          "transition-all",
          selectable && "cursor-pointer hover:shadow-md",
          isSelected && "border-primary ring-2 ring-primary/20"
        )}
        onClick={() => selectable && toggleSelect(report.id)}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            {selectable && (
              <Checkbox
                checked={isSelected}
                className="mt-1"
              />
            )}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Badge className="bg-success/10 text-success border-success/20 gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    Firmato
                  </Badge>
                  <Badge variant="outline">{report.serviceName}</Badge>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/doctor/report/${report.id}`);
                  }}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="flex items-center gap-2 mb-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="font-semibold">{patient?.name}</span>
              </div>
              
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  <span>Esame: {format(new Date(report.examDate), "dd/MM/yyyy")}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Shield className="h-3 w-3" />
                  <span>Firmato: {format(new Date(report.signature.signedAt), "dd/MM HH:mm")}</span>
                </div>
              </div>

              {report.status === 'sent' && report.sentTo && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Inviato a:</span>
                  {report.sentTo.includes('patient') && (
                    <Badge variant="outline" className="text-xs gap-1">
                      <Mail className="h-3 w-3" />Paziente
                    </Badge>
                  )}
                  {report.sentTo.includes('fascicolo') && (
                    <Badge variant="outline" className="text-xs gap-1">
                      <Building2 className="h-3 w-3" />FSE
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold">Referti Completati</h1>
        <p className="text-muted-foreground">Gestisci i referti firmati e inviati</p>
      </div>

      <Tabs defaultValue="ready">
        <TabsList>
          <TabsTrigger value="ready" className="gap-2">
            <Send className="h-4 w-4" />
            Da Inviare ({signedReports.length})
          </TabsTrigger>
          <TabsTrigger value="sent" className="gap-2">
            <CheckCircle2 className="h-4 w-4" />
            Inviati ({sentReports.length})
          </TabsTrigger>
        </TabsList>

        {/* Ready to Send */}
        <TabsContent value="ready" className="mt-6 space-y-4">
          {signedReports.length > 0 && (
            <Card className="sticky top-0 z-10">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <Checkbox
                      checked={selectedReports.length === signedReports.length && signedReports.length > 0}
                      onCheckedChange={selectAll}
                    />
                    <span className="text-sm">
                      {selectedReports.length > 0 
                        ? `${selectedReports.length} selezionati`
                        : "Seleziona tutto"
                      }
                    </span>
                  </div>
                  <Button
                    variant="premium"
                    onClick={handleSendReports}
                    disabled={selectedReports.length === 0 || isSending}
                    className="gap-2"
                  >
                    {isSending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                    Invia Selezionati
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <ScrollArea className="h-[500px]">
            <div className="space-y-4 pr-4">
              {signedReports.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Send className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">Nessun referto da inviare</p>
                  </CardContent>
                </Card>
              ) : (
                signedReports.map((report) => (
                  <ReportCard key={report.id} report={report} selectable />
                ))
              )}
            </div>
          </ScrollArea>
        </TabsContent>

        {/* Sent */}
        <TabsContent value="sent" className="mt-6">
          <ScrollArea className="h-[500px]">
            <div className="space-y-4 pr-4">
              {sentReports.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <CheckCircle2 className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">Nessun referto inviato</p>
                  </CardContent>
                </Card>
              ) : (
                sentReports.map((report) => (
                  <ReportCard key={report.id} report={report} />
                ))
              )}
            </div>
          </ScrollArea>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DoctorCompletedReports;
